import clsx from "clsx";
import { X } from "lucide-react";
import { type ReactNode, useId, useRef, useState } from "react";

import { ConfirmDialog } from "./ConfirmDialog";
import { motion, Presence, useClosing } from "./presence";
import { IconButton } from "./ui";
import { useDismiss } from "./useDismiss";
import { useFocusTrap } from "./useFocusTrap";
import { t } from "../i18n/i18n";

/** Fenêtre modale (R9) : voile `bg-ink/20` en fondu, fenêtre `rounded-xl` en léger zoom, à l'ouverture comme à la
 *  fermeture (à entourer de `Presence`). En-tête : titre en 12 px semi-gras, sous-titre en 11,5 px, `actions`
 *  avant la croix (`IconButton size="sm"`). Corps défilant : `DialogBody` pour la gouttière standard (`px-4
 *  py-4`). Pied : « Annuler » (`Button variant="secondary"`) puis l'action, alignés à droite. Échap et clic sur le
 *  voile ferment (pile d'Échap partagée). Focus (`useFocusTrap`) : il entre dans la fenêtre à l'ouverture (premier
 *  élément tabulable, sauf un champ `autoFocus` déjà au point), Tab boucle dedans, et il revient à l'élément d'avant
 *  à la fermeture. Plan : z-50.
 *
 *  `dirty` : la fenêtre contient une saisie non enregistrée ; Échap, la croix et le clic sur le voile demandent
 *  d'abord « Abandonner les modifications ? » (« Revenir à la saisie » par défaut), comme le registre des saisies
 *  (audit de persistance M4). Le bouton « Annuler » du pied reste un abandon explicite. */
export function Dialog({
  title,
  subtitle,
  actions,
  onClose,
  children,
  footer,
  width = 520,
  dirty = false,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Boutons d'en-tête, avant la croix (`IconButton size="sm"`). */
  actions?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** Largeur maximale en pixels. */
  width?: number;
  /** Saisie non enregistrée : la fermeture par Échap, la croix ou le voile demande confirmation. */
  dirty?: boolean;
}) {
  const closing = useClosing();
  const [asking, setAsking] = useState(false);
  const requestClose = () => (dirty ? setAsking(true) : onClose());
  useDismiss(!closing && !asking, requestClose);
  const titleId = useId();
  const box = useRef<HTMLElement>(null);
  useFocusTrap(box);
  return (
    <>
      <div
        className={clsx("fixed inset-0 top-(--gx-strip-h) z-50 flex items-center justify-center bg-ink/20 p-4", motion("fade", closing))}
        onClick={closing ? undefined : requestClose}
      >
        <section
          ref={box}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className={clsx(
            "flex max-h-[85vh] w-full flex-col overflow-hidden rounded-xl border border-line bg-surface shadow-float outline-none",
            motion("zoom", closing),
          )}
          style={{ maxWidth: width }}
          onClick={(e) => e.stopPropagation()}
        >
          <header className="flex min-h-10 shrink-0 items-center justify-between gap-3 border-b border-line py-1.5 pr-2 pl-4">
            <div className="min-w-0">
              <h2 id={titleId} className="text-ui leading-snug font-semibold text-ink">
                {title}
              </h2>
              {subtitle && <p className="text-meta leading-snug text-ink-muted">{subtitle}</p>}
            </div>
            <div className="flex shrink-0 items-center gap-0.5">
              {actions}
              <IconButton size="sm" onClick={requestClose} title={t("Fermer")} aria-label={t("Fermer")}>
                <X size={16} />
              </IconButton>
            </div>
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
          {footer && <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-line px-4 py-3">{footer}</footer>}
        </section>
      </div>
      {/* Hors du voile : un clic dans la confirmation (rendue dans un portail) ne remonte pas jusqu'à lui. */}
      <Presence show={asking}>
        {asking && (
          <ConfirmDialog
            title={t("Abandonner les modifications ?")}
            confirmLabel={t("Abandonner")}
            cancelLabel={t("Revenir à la saisie")}
            onConfirm={() => {
              setAsking(false);
              onClose();
            }}
            onCancel={() => setAsking(false)}
          >
            {t("Ce que vous avez saisi dans cette fenêtre n'est pas enregistré.")}
          </ConfirmDialog>
        )}
      </Presence>
    </>
  );
}

/** Corps d'une fenêtre dans la gouttière standard (`px-4 py-4`), contenu espacé de 12 px. */
export function DialogBody({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={clsx("space-y-3 px-4 py-4", className)}>{children}</div>;
}
